<template>
    <div :class="width">
        <div :class="computedBoxClass" @click="onBoxClick">
            <input
                ref="fileInput"
                type="file"
                class="sr-only"
                :readonly="readonly"
                :accept="accept"
                :disabled="disabled"
                @focus="isFocused = true"
                @blur="isFocused = false"
                @change="onChange"
            />

            <div class="flex-1 flex items-center gap-2 min-w-0">
                <documentIcon
                    v-if="fileName"
                    width="16"
                    height="16"
                    color="text-content-basic-2"
                />
                <span
                    :class="computedTextClass"
                >
                    {{ fileName || placeholder }}
                </span>
            </div>

            <div v-if="!disabled && !readonly" @click.stop>
                <!-- LG -->
                <designButton
                    v-if="normalizedSize === 'LG'"
                    btnType="SECONDARY"
                    btnSize="SM"
                    class="min-w-[4rem]"
                    :btnText="modelValue ? editText : uploadText"
                    @click-event="onButtonClick"
                >
                    <template v-if="!modelValue" #front-icon>
                        <UploadIcon
                            width="16"
                            height="16"
                            color="text-content-basic-1"
                        />
                    </template>
                </designButton>
                <!-- SM -->
                <button
                    v-else
                    type="button"
                    class="flex items-center justify-center"
                    @click="onButtonClick"
                >
                    <template v-if="fileName">
                        <deleteIcon
                            width="16"
                            height="16"
                            weight="THIN"
                            color="text-content-basic-2"
                        />
                    </template>
                    <template v-else>
                        <UploadIcon
                            width="16"
                            height="16"
                            color="text-content-basic-2"
                        />
                    </template>
                </button>
            </div>
        </div>
        <p v-if="error" class="mt-1 c_alert_message">
            {{ error }}
        </p>
    </div>
</template>

<script>
import designButton from '@/components/design-system/design-button.vue';
import UploadIcon from '@/components/design-system/icon/upload-icon.vue';
import documentIcon from '@/components/design-system/icon/document.vue';
import deleteIcon from '@/components/design-system/icon/delete.vue';

export default {
    name: 'design-file-upload',
    components: {
        designButton,
        UploadIcon,
        documentIcon,
        deleteIcon,
    },
    props: {
        modelValue: { type: [File, String, Object, null], default: null },
        placeholder: { type: String, default: 'Upload an file' },
        size: {
            type: String,
            default: 'LG',
            validator: (v) => ['SM', 'LG'].includes(String(v).toUpperCase()),
        },
        uploadText: { type: String, default: 'Upload' },
        editText: { type: String, default: 'Edit' },
        width: { type: String, default: 'w-full' },
        accept: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        error: { type: [Boolean, String], default: false },
        fileName: { type: String, default: '' },
        size: { type: String, default: 'SM' }
    },
    emits: ['update:modelValue', 'change', 'input', 'delete'],
    data() {
        return {
            boxBaseClass: [
                'flex items-center w-full gap-2 rounded-02 border pl-3 transition-all',
            ],
            boxClassMap: {
                LG: 'h-11 pr-1',
                SM: 'h-9 pr-3',
            },
            textClassMap: {
                LG: 'font-body-sm-regular',
                SM: 'font-caption-md-regular',
            },
            isFocused: false,
        };
    },
    computed: {
        normalizedSize() {
            return String(this.size).toUpperCase();
        },
        computedBoxClass() {
            const sizeClass = this.boxClassMap[this.normalizedSize] || '';
            const bgClass = this.disabled || this.readonly
                ? 'bg-background-basic-3'
                : 'bg-white';
            const borderClass = this.error
                ? 'border-accent-red-3'
                : this.isFocused
                    ? 'border-primary-3 ring-2'
                    : 'border-border-basic-2';
            const cursorClass = this.disabled
                ? 'cursor-not-allowed'
                : !this.fileName && !this.readonly
                    ? 'cursor-pointer'
                    : 'cursor-default';

            return [...this.boxBaseClass, sizeClass, bgClass, borderClass, cursorClass].join(' ');
        },
        computedTextClass() {
            const textClass = this.textClassMap[this.normalizedSize] || '';
            const textColorClass = this.fileName ? 'text-primary-3' : 'text-content-basic-3';
            return ['truncate', textClass, textColorClass].join(' ');
        },
    },
    methods: {
        triggerFile() {
            if (this.disabled || this.readonly) return;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        onBoxClick() {
            if (this.disabled || this.readonly || this.fileName) return;
            this.triggerFile();
        },
        onButtonClick() {
            if (!this.fileName) {
                this.triggerFile();
                return;
            }

            if (this.normalizedSize === 'LG') {
                this.triggerFile();
                return;
            }
            
            this.$emit('delete');
            return;
        },
        onChange(e) {
            const input = e.target;
            const file = input.files && input.files[0];
            if (!file) {
                input.value = '';
                return;
            }
            this.$emit('update:modelValue', file);
            this.$emit('change', file);
            this.$emit('input', file);
            input.value = '';
        },
    },
};
</script>

<style scoped>
</style>